import React, { useState, FC, Dispatch, SetStateAction } from "react";
import { SelectChangeEvent } from "@mui/material";
import { useQueryClient, useMutation } from "react-query";
import api from "../../../../api/api";
import {
  postStatusChange,
  postCloneQuote,
  postGlassOrderDate,
  archiveQuotes,
  unArchiveQuotes,
} from "../../../../api/DzoneApi";
import { sendOrderAcknowledgement } from "../../../../api/pdf";
import { useNavigate } from "react-router-dom";
import { QuoteObj, UserObj, Overwrite } from "../../../../GlobalTypes";
import { TableState } from "../../../../pages/DealerZone/DealerZone";

export type AdminQuoteObj = Overwrite<QuoteObj, { userId: UserObj }>;

export interface DealerState {
  factor: number;
  region: string;
  terms: string;
  dealerTax: number;
}

export interface AdminOrderNumsState {
  salesOrder: string;
  invoiceNumber: string;
  invoiceDate: string;
  depositDate: string;
  glassOrderDate: string;
}

interface SelectorArg {
  searchTerm: string;
  isSearch: boolean;
  setIsSearch: Dispatch<SetStateAction<boolean>>;
  userId: string;
  showTab: string;
  data:
    | string
    | {
        currentPage: AdminQuoteObj[] | QuoteObj[];
        totalResults?: number;
        missingInCommaList?: string[];
      };
  isFetching: boolean;
  setState: Dispatch<SetStateAction<TableState>>;
  setReSearch: Dispatch<SetStateAction<boolean>>;
}

function useQuotesTableSelector(args: SelectorArg) {
  const {
    searchTerm,
    isSearch,
    setIsSearch,
    userId,
    showTab,
    data,
    isFetching,
    setState,
    setReSearch,
  } = args;

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const role = JSON.parse(sessionStorage.getItem("role") || JSON.stringify(""));

  const [selectedQuotes, setSelectedQuotes] = useState<
    QuoteObj[] | AdminQuoteObj[]
  >([]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setState((prevState) => ({ ...prevState, searchTerm: value }));
  };

  const handleFilterByChange = (event: SelectChangeEvent<string>) =>
    setState((prevState) => ({ ...prevState, filterBy: event.target.value }));

  const handleShowLeadsOnly = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;
    setState((prevState) => ({ ...prevState, showLeadsOnly: checked, page: 0 }));

    if (!checked && !searchTerm) {
      setIsSearch(false);
      return;
    }

    if (isSearch) setReSearch((prev) => !prev);

    setIsSearch(true);
  };

  const onKeyDown = (event: React.KeyboardEvent) =>
    event.key === "Enter" && filterSearch();

  const filterSearch = () => {
    if (!searchTerm) {
      setIsSearch(false);
      return;
    }
    setState((prevState) => ({ ...prevState, page: 0 }));

    if (isSearch) setReSearch((prev) => !prev);

    setIsSearch(true);
  };

  const handleReset = () => {
    setIsSearch(false);
    setState((prevState) => ({ ...prevState, page: 0, searchTerm: "", showLeadsOnly: false }));

    queryClient.invalidateQueries(showTab);
  };

  const [adminOrderNums, setAdminOrderNums] = useState<
    AdminOrderNumsState | {}
  >({
    salesOrder: "",
    invoiceNumber: "",
    invoiceDate: "",
    depositDate: "",
    glassOrderDate: "",
  });

  const [sendOrderAck, setSendOrderAck] = useState("no");

  const [orderAckErr, setOrderAckErr] = useState(false);

  const handleCheckOrderAck = (event: SelectChangeEvent<string>) =>
    setSendOrderAck(event.target.value);

  const handleAdminChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setAdminOrderNums({ ...adminOrderNums, [name]: value });
  };

  const [soMatch, setSoMatch] = useState(false);

  const adminButtonCommonLogic = async (
    endpoint: string,
    body: { [key: string]: string | Date }
  ) => {
    if (selectedQuotes.length !== 1) return;

    const response = await api.put(endpoint, {
      quoteId: selectedQuotes[0]._id,
      soMatch,
      ...body,
    });

    if (!soMatch && response.data === "Sales Order already exists") {
      return setSoMatch(true);
    }
    setSoMatch(false);

    if (response.data.error) {
      return alert("Could Not Update Quote");
    }

    if (endpoint === "/api/addSalesOrder" && sendOrderAck === "yes") {
      setOrderAckErr(false);
      await sendOrderAcknowledgement(
        selectedQuotes[0]._id,
        body.salesOrder as string
      );
    }

    setAdminOrderNums({});
    setSelectedQuotes([]);
    handleAdminClose();

    return queryClient.invalidateQueries("Orders");
  };

  const adminGlassOrderDate = async () => {
    if (!selectedQuotes.length) return;

    if ("glassOrderDate" in adminOrderNums && adminOrderNums.glassOrderDate) {
      const ids = selectedQuotes.map((itm) => itm._id);

      const response = await postGlassOrderDate(
        ids,
        adminOrderNums.glassOrderDate
      );

      if (response.data.error) {
        alert("Could Not Update Quote");
        return;
      }

      setAdminOrderNums({});
      handleAdminClose();

      return queryClient.invalidateQueries("Orders");
    }

    return;
  };

  const [adminOrderType, setAOT] = useState("");

  const [openAdminModal, setOAM] = useState(false);

  const [warnModal, setWarnModal] = useState(false);

  const handleWarnModalClose = () => setWarnModal(false);

  const handleAdminOpen = (type: string, showWarningModal?: boolean) => {
    if (showWarningModal) {
      setWarnModal(true);
      return;
    }
    setAOT(type);
    setSoMatch(false);
    if (type === "Sales" && selectedQuotes[0]?.salesOrder) {
      setAdminOrderNums((prev) => ({
        ...prev,
        salesOrder: selectedQuotes[0].salesOrder,
      }));
    }
    setOAM(true);
  };
  const handleAdminClose = () => {
    setOAM(false);
    setWarnModal(false);
    setAdminOrderNums({
      salesOrder: "",
      invoiceNumber: "",
      invoiceDate: "",
      depositDate: "",
      glassOrderDate: "",
    });
  };

  const salesOrderMutation = useMutation(() =>
    adminButtonCommonLogic("/api/addSalesOrder", {
      salesOrder:
        "salesOrder" in adminOrderNums ? adminOrderNums.salesOrder : "",
    })
  );

  const invoiceMutation = useMutation(() =>
    adminButtonCommonLogic("/api/addInvoice", {
      invoiceNumber:
        "invoiceNumber" in adminOrderNums ? adminOrderNums.invoiceNumber : "",
      invoiceDate:
        "invoiceDate" in adminOrderNums && adminOrderNums.invoiceDate
          ? new Date(
              adminOrderNums.invoiceDate.replace(/-/g, "/")
            ).toLocaleDateString("en-US")
          : new Date(),
    })
  );

  const depositDateMutation = useMutation(() =>
    adminButtonCommonLogic("/api/addDepositDate", {
      depositDate:
        "depositDate" in adminOrderNums ? adminOrderNums.depositDate : "",
    })
  );

  const glassOrderMutation = useMutation(() => adminGlassOrderDate());

  const mutation = useMutation(
    async (status: string) => {
      if (!status) return null;

      setLoadingModal(true);
      const ids = selectedQuotes.map((itm) => itm._id);
      const res = await postStatusChange(showTab, ids, status);

      if (res.data === "Status Changed") {
        if (isSearch && showTab === "Quotes") {
          setIsSearch(false);
          setState((prevState) => ({ ...prevState, page: 0 }));
        }
        resetAfterFetch();
      }
      if (isSearch && showTab === "Orders") {
        setIsSearch(true);
        return queryClient.invalidateQueries("Orders");
      }

      queryClient.invalidateQueries("Quotes");
      return queryClient.invalidateQueries("Orders");
    },
    {
      onError: (error) => {
        console.error("Error in mutation:", error);
      },
    }
  );

  const archiveMutation = useMutation(async () => {
    setLoadingModal(true);

    function archiveReset() {
      setSelectedQuotes([]);
      setLoadingModal(false);
      handleCloseModalArchive();
      return queryClient.invalidateQueries("Quotes");
    }

    const ids = selectedQuotes.map((itm) => itm._id);

    if (selectedQuotes[0].status === "Archived") {
      const res = await unArchiveQuotes(ids);

      if (res.data === "unarchived") {
        return archiveReset();
      }
    } else {
      const res = await archiveQuotes(ids, archiveReason, archiveReasonText);

      if (res.data === "archived") {
        return archiveReset();
      }
    }
  });

  const resetAfterFetch = () => {
    setSelectedQuotes([]);
    setLoadingModal(false);
    handleCloseModal();
  };

  const handleViewClick = (quote: QuoteObj | AdminQuoteObj) => {
    localStorage.clear();
    sessionStorage.setItem("quoteId", JSON.stringify(quote._id));
    sessionStorage.setItem("quoteNumber", JSON.stringify(quote.quoteNumber));

    const userIdView =
      typeof quote.userId === "object" ? quote.userId._id : quote.userId;

    if (role === "Admin" || role === "Inside Sales") {
      navigate(`/config/${userIdView}`, {
        state: { userId: userIdView, resetStore: true },
      });
    } else {
      const orderSummaryId = role === "Dealer" ? userId : userIdView;

      if (quote.status === "Archived" || quote.status !== "Quote") {
        navigate(`/orderSummary/${orderSummaryId}`, {
          state: { userId: userIdView },
        });
      } else {
        navigate(`/config/${orderSummaryId}`, {
          state: { userId: orderSummaryId },
        });
      }
    }
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setState((prevState) => ({ ...prevState, page: newPage }));

    isSearch && setIsSearch(true);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    let RPP = parseInt(event.target.value, 10);
    setState((prevState) => ({ ...prevState, rowsPerPage: RPP }));

    isSearch && setIsSearch(true);
  };

  const handleCheck = (
    event: React.MouseEvent<any>,
    rowQuote: QuoteObj | AdminQuoteObj
  ) => {
    if (showTab === "Quotes" && selectedQuotes.length) {
      if (
        selectedQuotes[0].status === "Quote" &&
        rowQuote.status === "Archived"
      ) {
        return alert("Can't select Archive when Quote is selected");
      }

      if (
        selectedQuotes[0].status === "Archived" &&
        rowQuote.status === "Quote"
      ) {
        return alert("Can't select Quote when Archive is selected");
      }
    }

    const mappedIds = selectedQuotes.map((unit) => unit._id);
    const quoteIndex = mappedIds.indexOf(rowQuote._id);
    let newSelQuotes: any = [];

    if (quoteIndex === -1) {
      const isShift =
        "shiftKey" in event.nativeEvent ? event.nativeEvent.shiftKey : false;

      if (
        isShift &&
        !isFetching &&
        typeof data === "object" &&
        "currentPage" in data &&
        data?.currentPage
      ) {
        const idsSelected = [...mappedIds, rowQuote._id];
        let startIdx = 0;
        for (let i = 0; i < data.currentPage.length; i++) {
          const quote = data.currentPage[i];
          if (idsSelected.includes(quote._id)) {
            startIdx = i;
            break;
          }
        }

        let endIdx = 0;
        for (let j = data.currentPage.length - 1; j > startIdx; j--) {
          const quote = data.currentPage[j];
          if (idsSelected.includes(quote._id)) {
            endIdx = j;
            break;
          }
        }
        newSelQuotes = data.currentPage.slice(startIdx, endIdx + 1);
      } else {
        newSelQuotes = newSelQuotes.concat(selectedQuotes, rowQuote);
      }
    } else if (quoteIndex === 0) {
      newSelQuotes = newSelQuotes.concat(selectedQuotes.slice(1));
    } else if (quoteIndex === selectedQuotes.length - 1) {
      newSelQuotes = newSelQuotes.concat(selectedQuotes.slice(0, -1));
    } else if (quoteIndex > 0) {
      newSelQuotes = newSelQuotes.concat(
        selectedQuotes.slice(0, quoteIndex),
        selectedQuotes.slice(quoteIndex + 1)
      );
    }

    setSelectedQuotes(newSelQuotes);
  };

  const unCheckAll = () => {
    setSelectedQuotes([]);
  };

  const isSelected = (id: string) =>
    selectedQuotes.map((itm) => itm._id).indexOf(id) !== -1;

  const [loading, setLoading] = useState(false);

  const [duplicate, setDuplicate] = useState({
    open: false,
    error: false,
    quoteNumber: 0,
    user: "",
  });

  const [loadDuplicate, setLoadDuplicate] = useState(false);

  const openDuplicateModal = () => {
    if (selectedQuotes.length !== 1) return;

    const selectedQuote = selectedQuotes[0];

    const userId =
      typeof selectedQuote.userId === "object"
        ? selectedQuote.userId._id
        : selectedQuote.userId;

    setDuplicate({
      open: true,
      error: false,
      quoteNumber: selectedQuote.quoteNumber,
      user: userId || "",
    });
  };

  const closeDuplicateModal = () => {
    setDuplicate({ open: false, error: false, quoteNumber: 0, user: "" });
    setLoadDuplicate(false);
  };

  const [openModal, setOpenModal] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);

  const [archiveModal, setArchiveModal] = useState(false);

  const handleCloseModal = () => setOpenModal(false);

  const handleOpenModal = () => setOpenModal(true);

  const handleCloseModalArchive = () => setArchiveModal(false);

  const [openSaberis, setOpenSaberis] = useState(false);

  const handleCloseSaberis = () => setOpenSaberis(false);

  const handleOpenSaberis = () => setOpenSaberis(true);

  const handleOpenModalArchive = () =>
    selectedQuotes.length && setArchiveModal(true);

  const duplicateQuote = async () => {
    if (!duplicate.open || !duplicate.quoteNumber) return;
    setLoadDuplicate(true);

    const response = await postCloneQuote(
      duplicate.quoteNumber,
      duplicate.user
    );

    if (response.data.error) {
      setDuplicate((previous) => ({ ...previous, error: true }));
      setLoadDuplicate(false);
      return;
    }

    closeDuplicateModal();
    return queryClient.invalidateQueries("Quotes");
  };

  const [hoverId, setHoverId] = useState("");

  const [archiveReason, setArchiveReason] = useState("");

  const [archiveReasonText, setArchiveReasonText] = useState("");

  const handleChangeArchive = (event: SelectChangeEvent<string>) => {
    setArchiveReason(event.target.value);
  };

  const handleChangeTextBoxArchive = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setArchiveReasonText(event.target.value);
  };

  const mouseEnter = (unitId: string) => {
    setHoverId(unitId);
  };

  const mouseLeave = () => {
    if (!selectedQuotes.length) {
      setHoverId("");
    }
  };

  const adminModalObj =
    adminOrderType === "Sales"
      ? {
          modalText: "Add A Sales Order",
          mutation: salesOrderMutation,
          name: "salesOrder",
        }
      : adminOrderType === "Invoice"
      ? {
          modalText: "Add an Invoice Number",
          mutation: invoiceMutation,
          name: "invoiceNumber",
        }
      : adminOrderType === "Deposit"
      ? {
          modalText: "Add Date of Received Deposit",
          mutation: depositDateMutation,
          name: "depositDate",
        }
      : {
          modalText: "Add Glass Order Date",
          mutation: glassOrderMutation,
          name: "glassOrderDate",
        };

  const rows =
    !isFetching &&
    data !== "No Quotes Found" &&
    typeof data === "object" &&
    "currentPage" in data
      ? data.currentPage
      : [];

  return {
    handleChange,
    handleFilterByChange,
    handleShowLeadsOnly,
    onKeyDown,
    handleReset,
    orderAckErr,
    handleCheckOrderAck,
    handleAdminChange,
    openAdminModal,
    warnModal,
    handleWarnModalClose,
    handleAdminOpen,
    handleAdminClose,
    mutation,
    archiveMutation,
    handleViewClick,
    handleChangePage,
    handleChangeRowsPerPage,
    handleCheck,
    unCheckAll,
    isSelected,
    loading,
    setLoading,
    loadDuplicate,
    openDuplicateModal,
    closeDuplicateModal,
    openModal,
    loadingModal,
    archiveModal,
    handleOpenModal,
    openSaberis,
    handleCloseSaberis,
    handleOpenSaberis,
    handleOpenModalArchive,
    duplicateQuote,
    hoverId,
    handleChangeArchive,
    handleChangeTextBoxArchive,
    mouseEnter,
    mouseLeave,
    adminModalObj,
    filterSearch,
    selectedQuotes,
    role,
    adminOrderNums,
    soMatch,
    sendOrderAck,
    handleCloseModalArchive,
    archiveReason,
    archiveReasonText,
    duplicate,
    handleCloseModal,
    rows,
  };
}

export default useQuotesTableSelector;
